import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { getArticleQueryKey } from '@web/newsfeed/utils/getQueryKey';
import { Article } from '@web/newsfeed/types/Article';
import { QueryKey } from '@web/newsfeed/types/QueryKey';

export const useNewsfeedQueryCacheMutations = () => {
  const queryClient = useQueryClient();

  return useMemo(() => {
    const onAddArticleComment = (articleId: ID) => {
      const detailsQueryKey = getArticleQueryKey(articleId);

      const updateArticle = (article: Article): Article =>
        article.id === articleId
          ? { ...article, commentsCount: (article.commentsCount || 0) + 1, commentedByMe: true }
          : article;

      queryClient.setQueryData<Article>(detailsQueryKey, (cachedData) => {
        if (!cachedData) return;

        return updateArticle(cachedData);
      });

      void queryClient.cancelQueries([QueryKey.ARTICLE_SEARCH]);
      void queryClient.invalidateQueries([QueryKey.ARTICLE_SEARCH]);
    };

    const onDeleteArticleComment = (articleId: ID) => {
      const detailsQueryKey = getArticleQueryKey(articleId);

      // if we ever need to trim down number of requests here
      // we'd need server return `article.commented` after remove / add comment actions
      void queryClient.cancelQueries(detailsQueryKey);
      void queryClient.invalidateQueries(detailsQueryKey);
      void queryClient.cancelQueries([QueryKey.ARTICLE_SEARCH]);
      void queryClient.invalidateQueries([QueryKey.ARTICLE_SEARCH]);
    };

    return {
      onAddArticleComment,
      onDeleteArticleComment,
    };
  }, [queryClient]);
};
